import {TitleNav, Page, HCThemeType, Typography} from '@hconnect/uikit'
import {CircularProgress, makeStyles} from '@material-ui/core'
import {Box} from '@mui/material'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory, useLocation} from 'react-router-dom'

import Content from '../../Molecules/Content'
import SubHeader from '../../Molecules/SubHeader'
import {BulkCementDeliverOrderForm} from '../../OrderIntake/BulkCement/Deliver/BulkCementDeliverOrderForm'
import {BulkCementDeliverOrderLegacyForm} from '../../OrderIntake/BulkCement/Deliver/BulkCementDeliverOrderLegacyForm'
import {useOrderIntakeData} from '../../OrderIntake/hooks'
import {useMultiLoadOptionsBase} from '../../OrderIntake/Options/query'
import {useFeaturesState} from '../../Organisms/Features'

const useStyles = makeStyles((theme: HCThemeType) => {
  return {
    pageStyle: {
      [theme.breakpoints.up('md')]: {
        padding: `${theme.spacing(4)}px`
      },
      padding: 0
    }
  }
})

const BulkCementDeliveryOrderIntake: React.FC = () => {
  const {t} = useTranslation()
  const history = useHistory()
  const {getFeature} = useFeaturesState()
  const useLegacyDesign = getFeature('OrderIntakeUseLegacyDesign')
  const applyMaterialEnteredDescription = getFeature('OrderIntakeMaterialEnteredDescription')
  const applyContractItemDescription = getFeature('OrderIntakeContractItemDescription')

  const classes = useStyles()

  const {customerId} = useOrderIntakeData()

  // Check if we're in split load mode
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const isSplitLoadMode = searchParams.get('loadType') === 'splitLoad'

  // Only fetch multi-load options when in split load mode
  const {isFetching: isFetchingMultiLoadOptions, isError: isErrorMultiLoadOptions} =
    useMultiLoadOptionsBase(
      applyMaterialEnteredDescription,
      applyContractItemDescription,
      isSplitLoadMode ? customerId : undefined // Only fetch when in split load mode
    )

  const pageContent = useMemo(() => {
    // Only show loading for multi-load options when in split load mode
    if (isSplitLoadMode && isFetchingMultiLoadOptions) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <CircularProgress data-test-id="order-intake-loading-delivery-options" />
          <Box marginTop={2}>
            <Typography
              variant="body2"
              color="secondary"
              style={{textAlign: 'center', maxWidth: 350}}
            >
              {t('orderIntake.loadingDeliveryOptions')}
            </Typography>
          </Box>
        </Box>
      )
    }

    if (isSplitLoadMode && isErrorMultiLoadOptions) {
      return <div>{t('orderIntake.errorMessage')}</div>
    }

    return useLegacyDesign ? <BulkCementDeliverOrderLegacyForm /> : <BulkCementDeliverOrderForm />
  }, [isErrorMultiLoadOptions, isFetchingMultiLoadOptions, isSplitLoadMode, t, useLegacyDesign])

  return (
    <Content>
      <SubHeader>
        <TitleNav title={t('overview.scheduleNewDelivery')} onClick={() => history.goBack()} />
      </SubHeader>
      <Page
        data-test-id="delivery-order-intake-page"
        className={!useLegacyDesign ? classes.pageStyle : ''}
      >
        {pageContent}
      </Page>
    </Content>
  )
}

export {BulkCementDeliveryOrderIntake}
